import React, { useEffect, useState } from "react";
import { Box, Tab, TabList, Tabs, Typography } from "@mui/joy";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import Loading from "../components/Loading";
import PageError from "../components/PageError";
import { User } from "../common/types";
import { useReadUser } from "../services/api";

interface MetalMindAdminLayoutContextValue {
  user: User;
}

export const useMetalMindAdminLayoutContext = () =>
  useOutletContext<MetalMindAdminLayoutContextValue>();

const MetalMindAdminLayout: React.FC = () => {
  const readUser = useReadUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        const userData = await readUser();
        if (!active) return;
        setUser(userData);
      } catch (err: any) {
        if (!active) return;
        setError(err?.message || "Failed to load MetalMind Admin context.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [readUser]);

  const activeTab = location.pathname.endsWith("/users")
    ? "users"
    : "feature-flags";

  if (loading) {
    return <Loading />;
  }

  if (error && !user) {
    return <PageError>{error}</PageError>;
  }

  if (!user?.is_metalmind_admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <Page>
      <PageHeader>
        <Box>
          <Typography level="h1">MetalMind Admin</Typography>
          <Typography level="body-sm" color="neutral">
            Manage environment-level feature flags and MetalMind Admin access.
          </Typography>
        </Box>
      </PageHeader>

      <Tabs
        value={activeTab}
        onChange={(_, nextValue) => {
          if (typeof nextValue === "string") {
            navigate(`/admin/metalmind/${nextValue}`);
          }
        }}
      >
        <TabList sx={{ mb: 2 }}>
          <Tab value="feature-flags">Feature Flags</Tab>
          <Tab value="users">Users</Tab>
        </TabList>
      </Tabs>

      <Outlet context={{ user }} />
    </Page>
  );
};

export default MetalMindAdminLayout;
